import { useEffect, useState } from "react";

/**
 * Tiny module-level pub/sub for the AI concierge modal. Lets surfaces
 * outside the launcher (the hero search, prompt chips, anywhere else)
 * open the concierge — optionally with a pre-filled query that auto-sends
 * the moment the modal mounts.
 *
 * Why not Context: the modal lives at the bottom of the HomePage tree but
 * the hero is at the top; threading a context provider around the whole
 * page just to share two pieces of state is overkill. A module store
 * stays simple and SSR-safe (initial render reads default state, the
 * client hydrates and subscribes).
 */
export interface ConciergeState {
  open: boolean;
  /**
   * Monotonic counter so the modal's auto-send effect fires every time
   * a new query is dispatched, even if the text matches the previous one.
   */
  pendingNonce: number;
  pendingQuery: string | null;
  /**
   * The AI pill is on screen. The launcher steps aside while it is, so there
   * is one way into MaJourneys AI at a time.
   */
  floatingBarVisible: boolean;
  /**
   * The home hero's own search box has scrolled out of view. Only the home
   * page reports this; the AI pill waits for it there and ignores it elsewhere.
   */
  pastHero: boolean;
}

const initial: ConciergeState = {
  open: false,
  pendingNonce: 0,
  pendingQuery: null,
  floatingBarVisible: false,
  pastHero: false,
};
let state: ConciergeState = initial;
const listeners = new Set<(s: ConciergeState) => void>();

function setState(next: Partial<ConciergeState>): void {
  state = { ...state, ...next };
  for (const fn of listeners) fn(state);
}

export const conciergeActions = {
  open(): void {
    setState({ open: true });
  },
  openWith(query: string): void {
    const trimmed = query.trim();
    if (!trimmed) {
      setState({ open: true });
      return;
    }
    setState({
      open: true,
      pendingQuery: trimmed,
      pendingNonce: state.pendingNonce + 1,
    });
  },
  close(): void {
    setState({ open: false });
  },
  setFloatingBarVisible(visible: boolean): void {
    if (state.floatingBarVisible !== visible) setState({ floatingBarVisible: visible });
  },
  setPastHero(past: boolean): void {
    if (state.pastHero !== past) setState({ pastHero: past });
  },
  clearPending(): void {
    setState({ pendingQuery: null });
  },
};

export function useConciergeStore(): ConciergeState {
  const [snap, set] = useState<ConciergeState>(state);
  useEffect(() => {
    listeners.add(set);
    set(state);
    return () => {
      listeners.delete(set);
    };
  }, []);
  return snap;
}
